import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useDOMEventListener } from 'Src/feature/dom-event/useDOMEventListener';
import { FormField } from 'Src/feature/form/FormField';
import { SelectOptionsOf } from 'Src/feature/form/SelectOption';
import { ProvisionItemList } from 'Src/feature/provision/ProvisionItemList';
import { TerminalOption } from 'Src/feature/provision/provisionService';

import TerminalClaimedDialog from './TerminalClaimedDialog';
import TerminalListItem from './TerminalListItem';
import { useEmailWorkflow } from './useEmailWorkflow';

import styles from './TerminalSelect.module.scss';

const sectionId = 'TerminalSelect';
type TerminalRole = TerminalOption['role'];

const TerminalSelect: FC = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const workflow = useEmailWorkflow();

  const deviceTypeOptions: SelectOptionsOf<TerminalRole | 'all'> = [
    { value: 'all', label: t('labels.deviceType'), disabled: true },
    { value: 'POS', label: t('labels.pos') },
    { value: 'TimeClock', label: t('labels.timeClock') },
  ];

  const [roleFilter, roleFilterSet] = useState('all' as TerminalRole | 'all');
  const [isConfirmingKill, isConfirmingKillSet] = useState(false);


  const handleDlgClose= () => {
    isConfirmingKillSet(false);
  };

  const handleKillConfirmed = () => {
    if (workflow.data.current.terminal) {
      // augmenting computed data values
      // - must reset all required data that computes to "workflow.data.current.terminal"
      workflow.updateData({
        locations: workflow.data.locations.map((l) => ({
          ...l,
          terminals: l.terminals.map((term) => ({
            ...term,
            isProvisioned: term.id === workflow.data.current.terminal?.id
              ? false
              : term.isProvisioned
          })),
        })),
      });


      isConfirmingKillSet(false);
    } else {
      log.error('handleKillConfirmed()', '!emailWorkflow.currentTerminal');
    }
  };

  const selectTerminal = (terminalId: number) => {
    workflow.updateData({ terminalId });
  };

  const wantedTerminals = roleFilter === 'all'
    ? (workflow.data.current.location?.terminals || [])
    : (workflow.data.current.location?.terminals || []).filter(({ role }) => role === roleFilter);
  const availableTerminals = wantedTerminals.filter(({ isProvisioned }) => !isProvisioned);
  const claimedTerminals = wantedTerminals.filter(({ isProvisioned }) => isProvisioned);

  useEffect(() => {
    workflow.steps.canGoNext = !!workflow.data.terminalId;
  }, [workflow]);


  useDOMEventListener('emailWorkflow.onGoPrev', useCallback(() => {
    workflow.updateData({
      terminalId: null,
    });
  }, [workflow]));
  useDOMEventListener('emailWorkflow.onGoNext', useCallback((evt: CustomEvent<null>) => {
    if (workflow.data.current.terminal && workflow.data.current.terminal.isProvisioned) {
      evt.preventDefault();
      isConfirmingKillSet(true);
    }
  }, [workflow.data]));

  return (
    <Flex alignItems="center" gap="sm" fullHeight fullWidth>
      <Copy variant="body" weight="bold" color="secondary">
        {t('terminal.selector.instruction')}
      </Copy>

      <FormField
        type="select"
        name="popoverTerminalRoleFilter"
        className={styles.select}
        options={deviceTypeOptions}
        value={roleFilter}
        onChange={(_evt, values) => {
          roleFilterSet(values[0] as 'all' | TerminalRole);
        }}
        fullWidth
      />

      <Copy variant="body" weight="bold">{t('labels.devices')}</Copy>
      <Flex horizontal fullWidth>
        <ProvisionItemList data-testid="unclaimed-terminals" title={t('labels.available')}>
          {availableTerminals.map((term) => (
            <TerminalListItem
              data-testid="unclaimed-terminal-item"
              key={term.id}
              terminal={term}
              selected={workflow.data.terminalId === term.id}
              onClick={() => selectTerminal(term.id)}
            />
          ))}
        </ProvisionItemList>

        <ProvisionItemList data-testid="claimed-terminals" title={t('labels.claimed')}>
          {claimedTerminals.map((term) => (
            <TerminalListItem
              data-testid="claimed-terminal-item"
              key={term.id}
              terminal={term}
              selected={workflow.data.terminalId === term.id}
              onClick={() => selectTerminal(term.id)}
            />
          ))}
        </ProvisionItemList>
      </Flex>

      <TerminalClaimedDialog
        open={isConfirmingKill}
        onCancel={handleDlgClose}
        onClaimKilled={handleKillConfirmed}
        terminal={workflow.data.current.terminal}
      />
    </Flex>
  );
};

export default TerminalSelect;
